import React from 'react';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';

const GUIDE_ID = 'admin-notifications-help';
const ACCENT = '#0f766e';

const FlowStep = ({ n, title, body, tone = 'default' }) => {
  const bg = tone === 'user' ? '#ecfdf5' : tone === 'send' ? '#eff6ff' : '#f8fafc';
  const border = tone === 'user' ? '#a7f3d0' : tone === 'send' ? '#bfdbfe' : '#e2e8f0';
  const numBg = tone === 'user' ? '#059669' : tone === 'send' ? '#2563eb' : ACCENT;
  return (
    <div style={{
      flex: '1 1 140px', minWidth: 120, maxWidth: 180,
      background: bg, border: `1px solid ${border}`, borderRadius: 10,
      padding: '12px 12px 14px', textAlign: 'center',
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%', background: numBg, color: '#fff',
        fontWeight: 700, fontSize: 13, display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', marginBottom: 8,
      }}>{n}</div>
      <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>{body}</div>
    </div>
  );
};

const Arrow = () => (
  <div style={{
    flex: '0 0 auto', alignSelf: 'center', color: '#94a3b8',
    fontSize: 18, fontWeight: 700, padding: '0 2px',
  }} aria-hidden>→</div>
);

const ModeCard = ({ label, color, bg, border, title, body }) => (
  <div style={{
    flex: '1 1 180px', background: bg, border: `1px solid ${border}`,
    borderRadius: 10, padding: '14px 14px 16px',
  }}>
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '3px 8px',
      borderRadius: 4, background: color, color: '#fff', marginBottom: 8,
    }}>{label}</span>
    <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', marginBottom: 4 }}>{title}</div>
    <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.45 }}>{body}</div>
  </div>
);

/**
 * Graphical help for Admin → Notifications (Recipients + Email Log).
 */
const NotificationHelpModal = ({ onClose }) => {
  if (!onClose) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10050, padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 50px rgba(0,0,0,0.28)', overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 18px', background: ACCENT, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Notifications — how this works</h3>
            <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>Managed list · Auto audience · Email Log</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ListenButton compact guideId={GUIDE_ID} title="Admin Notifications Help" accent={ACCENT} dark />
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff',
                borderRadius: 6, width: 32, height: 32, cursor: 'pointer', fontSize: 18, lineHeight: 1,
              }}
              aria-label="Close"
            >×</button>
          </div>
        </div>

        <div style={{ padding: '18px 20px 22px', overflowY: 'auto', flex: 1 }}>
          <HelpWithVideoTour guideId={GUIDE_ID} title="Admin Notifications Help" accent={ACCENT}>
            <section style={{ marginBottom: 22 }}>
              <h4 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                The flow
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 6 }}>
                <FlowStep n="1" title="Audience is chosen" body="Either your managed list, or the job’s project/role rules." />
                <Arrow />
                <FlowStep n="2" title="Schedule fires" body="Cron or an event builds the email at the listed day/time." />
                <Arrow />
                <FlowStep n="3" title="Profile mute" body="Anyone can turn an email off under Profile → Notification Settings." tone="user" />
                <Arrow />
                <FlowStep n="4" title="Email Log" body="Every send is recorded under the Email Log tab." tone="send" />
              </div>
            </section>

            <section style={{ marginBottom: 22 }}>
              <h4 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Three delivery modes
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <ModeCard
                  label="Managed list"
                  color="#0b5ed7"
                  bg="#e7f1ff"
                  border="#b6d4fe"
                  title="You pick the people"
                  body="Only listed names get the email. Count shows “N on list”. Resource Allocation and Resource Plan Reminder work this way."
                />
                <ModeCard
                  label="Auto audience"
                  color="#047857"
                  bg="#ecfdf5"
                  border="#a7f3d0"
                  title="Job picks people"
                  body="Hold Digest emails project assignees; tracker reminders email Technical Coordinators. “Picked at send time” means healthy — this page is not the full roster."
                />
                <ModeCard
                  label="Required"
                  color="#475569"
                  bg="#f8fafc"
                  border="#e2e8f0"
                  title="Must deliver"
                  body="Welcome, ticket reply, @mention — no Profile toggle, by design."
                />
              </div>
            </section>

            <section style={{ marginBottom: 8 }}>
              <h4 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                How to change who gets an email
              </h4>
              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{
                  display: 'flex', gap: 12, padding: '12px 14px',
                  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, background: '#ccfbf1',
                    color: ACCENT, fontWeight: 800, fontSize: 14, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>A</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>Managed list</div>
                    <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.45, marginTop: 2 }}>
                      Expand the row to see names, or use <strong>Add or edit a user</strong> and flip the toggle on.
                    </div>
                  </div>
                </div>
                <div style={{
                  display: 'flex', gap: 12, padding: '12px 14px',
                  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, background: '#dbeafe',
                    color: '#2563eb', fontWeight: 800, fontSize: 14, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>B</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>Auto audience</div>
                    <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.45, marginTop: 2 }}>
                      Audience comes from project/role assignment — change who is on the job, or clear a Profile mute if someone stopped getting mail.
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section style={{ marginTop: 22, marginBottom: 8 }}>
              <h4 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Send now
              </h4>
              <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                Every row has <strong>Send now</strong>. Pick the people — search a portal user, or type an email
                including a shared mailbox such as Project Controls — and optionally add a copy line.
                On a managed list, <strong>Start from the list</strong> fills the current roster so you can add or drop names.
                The email goes out immediately. It does not change who is on the schedule.
                Emails built per person (hold digest, Friday schedule request, a stale tracker) give each chosen person their own copy;
                someone with nothing due is skipped and named on the screen. A few rows have no email to send on their own
                (a sign-in code, a ticket reply, the disk alert). The button says why. Afterward, <strong>Email Log</strong> is the real To line.
              </div>
            </section>

            <section style={{ marginTop: 22, marginBottom: 8 }}>
              <h4 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Friday schedule updates tab
              </h4>
              <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                One row per job that has schedule data. The <strong>On/Off</strong> box is the Friday 7:00 AM ET progress-update
                request for that job — the same switch as the job's <strong>Schedule → Update</strong> button. <strong>Audience</strong> says
                where the names come from: <em>P6 Upd Resp</em> (the Upd Resp field synced from P6), <em>My list</em> (rows you typed:
                a label, which part of the schedule, and the e-mails), or <em>Both</em>. <strong>Who gets it</strong> warns when a job
                is on but nobody would receive it. <strong>Set up</strong> opens the job's Update window to add people, pick their
                slice (WBS node, resource, activity prefix or the whole schedule), add an extra CC, and send yourself a test.
                Each person gets two PDFs filtered to their slice — the 3-week look-ahead with write-in boxes and the full
                schedule — with the job's PM and Project Controls in copy and replies going to Project Controls.
                Superadmins and admins see this tab; schedule editors can change a job from its own Schedule page.
              </div>
            </section>

            <p style={{
              margin: '16px 0 0', padding: '10px 12px', borderRadius: 8,
              background: '#fffbeb', border: '1px solid #fde68a',
              fontSize: 12, color: '#92400e', lineHeight: 1.45,
            }}>
              Tip: after a scheduled send, open <strong>Email Log</strong> for the real To list — that is ground truth.
            </p>
          </HelpWithVideoTour>
        </div>
      </div>
    </div>
  );
};

export default NotificationHelpModal;
export { GUIDE_ID as NOTIFICATIONS_HELP_GUIDE_ID };
